import { asNumber, type CollectionData, type FieldDef, type RecordData } from '@tomo/core';
import { Checkbox, cx, Popover } from '@tomo/ui';
import { useState, type KeyboardEvent } from 'react';
import { useProject } from '../../data/hooks.tsx';
import { FieldValue, Stars, VoteButton } from './display.tsx';
import { DIRECT_TYPES, InlineEditor, INLINE_TYPES, isPopoverType, PopoverEditor } from './editors.tsx';

const POPOVER_WIDTH: Partial<Record<FieldDef['type'], number>> = { select: 260, multiSelect: 280, person: 260, relation: 320, range: 320, color: 252 };

export interface FieldCellProps {
  record: RecordData;
  field: FieldDef;
  collection: CollectionData;
  variant?: 'cell' | 'panel';
  readOnly?: boolean;
  className?: string;
}

/** A value that can be edited in place: inline input, popover editor or direct interaction. */
export function FieldCell({ record, field, collection, variant = 'cell', readOnly, className }: FieldCellProps) {
  const project = useProject();
  const [editing, setEditing] = useState(false);
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);
  const type = field.type;

  if (DIRECT_TYPES.has(type)) {
    return (
      <div className={cx('field-cell field-cell--direct', className)} data-variant={variant} data-type={type}>
        {type === 'checkbox' ? (
          <Checkbox
            checked={record.values[field.id] === true}
            disabled={readOnly}
            aria-label={field.name}
            onCheckedChange={(c) => project.setValue(record.id, field.id, c || null)}
            onClick={(e) => e.stopPropagation()}
          />
        ) : type === 'rating' ? (
          <Stars value={asNumber(record.values[field.id])} max={field.config.max ?? 5} onChange={readOnly ? undefined : (v) => project.setValue(record.id, field.id, v)} />
        ) : (
          <VoteButton record={record} field={field} size={variant === 'panel' ? 'md' : 'sm'} />
        )}
      </div>
    );
  }

  if (editing && INLINE_TYPES.has(type)) {
    return <InlineEditor record={record} field={field} onDone={() => setEditing(false)} className={cx('field-cell field-cell--editing', className)} size={variant === 'panel' ? 'md' : 'sm'} />;
  }

  const canEdit = !readOnly && (INLINE_TYPES.has(type) || isPopoverType(type));
  const onKey = (e: KeyboardEvent) => {
    if (!canEdit) return;
    if (e.key === 'Enter' || e.key === 'F2') {
      e.preventDefault();
      setEditing(true);
    } else if ((e.key === 'Backspace' || e.key === 'Delete') && e.target === e.currentTarget) {
      e.preventDefault();
      project.setValue(record.id, field.id, null);
    }
  };

  return (
    <>
      <div
        ref={setAnchor}
        className={cx('field-cell', className)}
        data-variant={variant}
        data-type={type}
        data-editable={canEdit || undefined}
        data-editing={editing || undefined}
        tabIndex={canEdit ? 0 : undefined}
        role={canEdit ? 'button' : undefined}
        aria-label={canEdit ? `Edit ${field.name}` : undefined}
        // An inline editor focuses itself, so taking focus first would only flash the cell ring.
        onMouseDown={(e) => canEdit && INLINE_TYPES.has(type) && e.button === 0 && e.preventDefault()}
        onClick={(e) => {
          if (!canEdit) return;
          e.stopPropagation();
          setEditing(true);
        }}
        onKeyDown={onKey}
      >
        <FieldValue record={record} field={field} collection={collection} variant={variant} placeholder={variant === 'panel' ? 'Empty' : undefined} />
      </div>
      {isPopoverType(type) && editing && (
        <Popover
          anchor={anchor}
          open={editing}
          onOpenChange={setEditing}
          width={POPOVER_WIDTH[type]}
          padding="sm"
          placement="bottom-start"
          initialFocus={0}
        >
          <PopoverEditor record={record} field={field} collection={collection} onDone={() => setEditing(false)} />
        </Popover>
      )}
    </>
  );
}
